import { useEffect, useMemo } from 'react';
import { isArray, isObject } from 'lodash-es';
import { useQueryClient } from '@tanstack/react-query';
import { getRoundDataFromBlockchainResponse } from '../utils/blockchain-helpers.ts';
import { debugLog } from '../utils/debug.ts';
import { useBlockchainBlockNumber, useBlockchainCurrentRoundNumber } from '../contexts/blockchain';
import { useIsBlockchainVersion } from './useIsBlockchainVersion.ts';

type WagmiReadKeyMeta = {
  functionName?: unknown;
  args?: unknown;
};

function isGetRoundDataKey(
  key: readonly unknown[]
): key is ['readContract', WagmiReadKeyMeta, ...unknown[]] {
  if (!Array.isArray(key) || key.length < 2) return false;
  if (key[0] !== 'readContract') return false;
  const meta = key[1];
  if (!isObject(meta)) return false;
  const fn = (meta as WagmiReadKeyMeta).functionName;
  return fn === 'getRoundData';
}

function getRoundNumberFromKey(key: unknown): bigint | null {
  if (!isArray(key)) return null;
  if ((key as readonly unknown[]).length < 2) return null;
  const arr = key as readonly unknown[];
  const meta = arr[1] as WagmiReadKeyMeta | undefined;
  if (!isObject(meta)) return null;
  const args = meta.args;
  if (!isArray(args) || args.length === 0) return null;
  const value = args[0];
  // accept bigint or number that can be converted to bigint
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return BigInt(Math.floor(value));
  return null;
}

function isRoundCompleted(raw: unknown): boolean {
  if (!raw) return false;
  try {
    const parsed = getRoundDataFromBlockchainResponse(raw as $TSFixMe);
    const endPrices = parsed?.endPrices as Maybe<bigint[]>;
    if (!endPrices || endPrices.length === 0) return false;
    return !endPrices.every((v) => v === 0n);
  } catch {
    return false;
  }
}

export const useAutoBlockchainRoundDataUpdater = () => {
  const blockchainVersion = useIsBlockchainVersion();
  const queryClient = useQueryClient();
  const blockNumber = useBlockchainBlockNumber();
  const currentRoundNumber = useBlockchainCurrentRoundNumber();

  // Early return in B2B mode - no blockchain updates needed
  if (!blockchainVersion) {
    return;
  }

  const getAllRoundQueries = useMemo(() => {
    return () =>
      queryClient.getQueriesData({
        predicate: (q) => Array.isArray(q.queryKey) && isGetRoundDataKey(q.queryKey as $TSFixMe),
      });
  }, [queryClient]);

  useEffect(() => {
    if (!blockchainVersion || !blockNumber || !currentRoundNumber) return;

    const entries = getAllRoundQueries();

    if (entries.length === 0) return;

    const toInvalidate: bigint[] = [];

    for (const [key, data] of entries) {
      if (!Array.isArray(key) || !isGetRoundDataKey(key)) {
        continue;
      }

      const entryRoundNumber = getRoundNumberFromKey(key);

      if (!data || !entryRoundNumber || currentRoundNumber <= entryRoundNumber) {
        continue;
      }

      const completed = isRoundCompleted(data);

      if (!completed) {
        toInvalidate.push(entryRoundNumber);
        queryClient.invalidateQueries({
          queryKey: key,
          exact: true,
        });
      }
    }

    if (toInvalidate.length > 0) {
      const roundsList = toInvalidate.map((r) => r.toString()).join(', ');
      debugLog(
        `📡 [ROUND AUTO UPDATER] [${new Date().toISOString()}] Invalidated ${toInvalidate.length} getRoundData quer${
          toInvalidate.length === 1 ? 'y' : 'ies'
        } for rounds [${roundsList}] at block ${blockNumber}`
      );
    }
  }, [currentRoundNumber, blockchainVersion, blockNumber, getAllRoundQueries, queryClient]);
};
